import { AccrualMatchSuggestionDisplay } from "@common/schemas/domains/accrualReview/accrual-match-suggestion-display.schema";
import {
    aggregateMatches,
    getSuggestedMatchPreviewItems,
    type MatchSummaryItem,
} from "./aggregateMatches";
import {
    type CashDocumentMatch,
    cashDocumentMatchesToPreview,
    getCashLinkedDocumentIds,
} from "./cashDocumentMatches";

export interface TransferMatchPreviewSource {
    amount?: number;
    accrualAccountId?: string | null;
    accrualMatches?: readonly MatchSummaryItem[] | null;
    pairedTransfer?: { id: number } | null;
    transactionMatches?: readonly CashDocumentMatch[] | null;
    accrualMatchSuggestions?: readonly AccrualMatchSuggestionDisplay[] | null;
    isDateLocked?: boolean;
}

export type TransferMatchPreview =
    | { kind: "accrualMatch"; items: readonly MatchSummaryItem[] }
    | { kind: "suggestion"; items: AccrualMatchSuggestionDisplay[] }
    | { kind: "cashDocument"; items: readonly MatchSummaryItem[] }
    | { kind: "pairedTransfer" }
    | { kind: "empty" };

function getSuggestedDocumentId(
    suggestion: AccrualMatchSuggestionDisplay,
): string | null {
    return suggestion.invoice?.id ?? suggestion.bill?.id ?? null;
}

function getSuggestionsCoveringCashLinks({
    transactionMatches,
    accrualMatchSuggestions,
}: Pick<
    TransferMatchPreviewSource,
    "transactionMatches" | "accrualMatchSuggestions"
>): AccrualMatchSuggestionDisplay[] {
    const suggestedItems = getSuggestedMatchPreviewItems(
        accrualMatchSuggestions,
    );
    const linkedDocumentIds = new Set(
        getCashLinkedDocumentIds(transactionMatches),
    );
    if (linkedDocumentIds.size === 0) {
        return suggestedItems;
    }
    const linkedSuggestions = suggestedItems.filter((suggestion) => {
        const documentId = getSuggestedDocumentId(suggestion);
        return documentId != null && linkedDocumentIds.has(documentId);
    });
    const suggestedDocumentIds = new Set(
        linkedSuggestions.map(getSuggestedDocumentId),
    );
    const isEveryLinkSuggested = [...linkedDocumentIds].every((documentId) =>
        suggestedDocumentIds.has(documentId),
    );
    return isEveryLinkSuggested ? linkedSuggestions : [];
}

export function getTransferMatchPreview(
    source: TransferMatchPreviewSource,
): TransferMatchPreview {
    const accrualMatches = source.accrualMatches ?? [];
    if (aggregateMatches(accrualMatches).resolvedCount > 0) {
        return { kind: "accrualMatch", items: accrualMatches };
    }
    const canShowSuggestion =
        source.accrualAccountId == null &&
        source.pairedTransfer == null &&
        !source.isDateLocked;
    const suggestedItems = canShowSuggestion
        ? getSuggestionsCoveringCashLinks(source)
        : [];
    if (suggestedItems.length > 0) {
        return { kind: "suggestion", items: suggestedItems };
    }
    const cashDocumentItems = cashDocumentMatchesToPreview({
        matches: source.transactionMatches,
        fallbackAmount: Math.abs(source.amount ?? 0),
    });
    if (cashDocumentItems.length > 0) {
        return { kind: "cashDocument", items: cashDocumentItems };
    }
    if (source.pairedTransfer != null) {
        return { kind: "pairedTransfer" };
    }
    return { kind: "empty" };
}
